import React, { useEffect, useState, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const AnugyaROICalc = () => {
  const reportRef = useRef();
  const [deploymentMode, setDeploymentMode] = useState("cloud");

  const [requests, setRequests] = useState(5000);
  const [cost, setCost] = useState(250);
  const [reduction, setReduction] = useState(30);
  const [employees, setEmployees] = useState(120);
  const [leakage, setLeakage] = useState(200000);

  const [report, setReport] = useState({
    currentCost: 0,
    monthlySavings: 0,
    annualSavings: 0,
    riskReduction: 0,
    productivity: 0,
    roi: 0,
    platformCost: 0,
    deploymentText: "Cloud",
    deploymentInsight: "",
    recommendation: "",
  });

  const formatINR = (num) => {
    return `₹${Number(num).toLocaleString("en-IN")}`;
  };

  useEffect(() => {
    calculateROI();
  }, [requests, cost, reduction, employees, leakage, deploymentMode]);

  const calculateROI = () => {
    const currentCost = requests * cost;

    const monthlySavings = (currentCost * reduction) / 100;

    const annualSavings = monthlySavings * 12;

    const riskReduction = (leakage * reduction) / 100;

    const productivity = Math.round((employees * reduction) / 10);

    let platformCost = 0;
    let deploymentText = "";
    let deploymentInsight = "";

    if (deploymentMode === "cloud") {
      platformCost = Math.round(requests * 0.8 + employees * 120);

      deploymentText = "Cloud";

      deploymentInsight =
        "Cloud deployment enables faster rollout, lower upfront investment and easier scaling across branches and departments.";
    } else {
      platformCost = Math.round(
        requests * 1.8 + employees * 350 + 250000
      );

      deploymentText = "On-Premise";

      deploymentInsight =
        "On-Premise deployment is ideal for banks, NBFCs and regulated enterprises requiring internal hosting, tighter governance and stronger regulatory alignment.";
    }

    const roi = Math.round(
      ((monthlySavings + riskReduction) / platformCost) * 100
    );

    let recommendation = "";

    if (roi > 800) {
      recommendation =
        "Excellent ROI potential. Your organization is highly suitable for governed workflow automation, secure document orchestration and consent-driven operational infrastructure.";
    } else if (roi > 400) {
      recommendation =
        "Strong ROI potential detected. Anugya can significantly improve workflow governance, operational speed and compliance visibility.";
    } else {
      recommendation =
        "Moderate ROI potential. Start with one department or workflow pilot to demonstrate measurable operational improvement.";
    }

    setReport({
      currentCost,
      monthlySavings,
      annualSavings,
      riskReduction,
      productivity,
      roi,
      platformCost,
      deploymentText,
      deploymentInsight,
      recommendation,
    });
  };

  const downloadPDF = async () => {
    const element = reportRef.current;

    const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("Anugya-ROI-Report.pdf");
    };

    const shareReport = async () => {
    const shareData = {
        title: "Anugya ROI Report",
        text: `ROI: ${report.roi}% | Savings: ${formatINR(
        report.annualSavings
        )}`,
        url: window.location.href,
    };

    try {
        if (navigator.share) {
        await navigator.share(shareData);
        } else {
        await navigator.clipboard.writeText(
            `${shareData.title}\n${shareData.text}\n${shareData.url}`
        );
        alert("Report copied to clipboard");
        }
    } catch (err) {
        console.log(err);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-slate-100 font-[Inter]">
      {/* HEADER */}
      <section className="sticky top-0 z-50 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-700 px-4 py-6 text-white shadow-2xl">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-extrabold tracking-tight">
            Anugya ROI Calculator
          </h1>

          <p className="text-sm leading-relaxed text-blue-100">
            Calculate operational savings, governance value, compliance
            reduction and efficiency impact using Anugya Consent OS.
          </p>
        </div>
      </section>

      {/* HERO */}
      <section className="px-4 py-5">
        <div className="rounded-[28px] border border-white/40 bg-white/80 p-5 shadow-2xl backdrop-blur-xl">
          <div className="flex flex-col gap-4">
            <div className="w-fit rounded-full bg-blue-100 px-4 py-2 text-xs font-bold text-blue-700">
              BFSI + MSME + Enterprise Governance
            </div>

            <h2 className="text-4xl font-extrabold leading-tight text-slate-900">
              Discover Hidden Operational Leakage
            </h2>

            <p className="text-sm leading-relaxed text-slate-600">
              Estimate how much your organization can save using governed
              document access, secure workflows, consent logs and intelligent
              verification systems.
            </p>
          </div>
        </div>
      </section>

      {/* INPUTS */}
      <section className="px-4 pb-6">
        <div className="rounded-[30px] bg-white p-5 shadow-2xl">
          <div className="mb-2">
            <h2 className="text-2xl font-extrabold text-yellow-600">
              Organization Inputs
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Adjust sliders to generate ROI report
            </p>
          </div>

          <div className="space-y-7">
            {/* REQUESTS */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-semibold">
                  Monthly Document Requests / Cases
                </label>

                <div className="font-bold text-blue-700">
                  {requests.toLocaleString("en-IN")}
                </div>
              </div>

              <input
                type="range"
                min="100"
                max="100000"
                value={requests}
                onChange={(e) => setRequests(Number(e.target.value))}
                className="w-full accent-blue-600"
              />
            </div>

            {/* COST */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-semibold">
                  Avg Operational Cost Per Request (₹)
                </label>

                <div className="font-bold text-blue-700">
                  {formatINR(cost)}
                </div>
              </div>

              <input
                type="range"
                min="10"
                max="5000"
                value={cost}
                onChange={(e) => setCost(Number(e.target.value))}
                className="w-full accent-blue-600"
              />
            </div>

            {/* REDUCTION */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-semibold">
                  Expected Efficiency Gain (%)
                </label>

                <div className="font-bold text-blue-700">
                  {reduction}%
                </div>
              </div>

              <input
                type="range"
                min="5"
                max="90"
                value={reduction}
                onChange={(e) => setReduction(Number(e.target.value))}
                className="w-full accent-blue-600"
              />
            </div>

            {/* EMPLOYEES */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-semibold">
                  Employees / Agents Handling Documents
                </label>

                <div className="font-bold text-blue-700">
                  {employees}
                </div>
              </div>

              <input
                type="range"
                min="5"
                max="5000"
                value={employees}
                onChange={(e) => setEmployees(Number(e.target.value))}
                className="w-full accent-blue-600"
              />
            </div>

            {/* LEAKAGE */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-semibold">
                  Estimated Monthly Leakage / Compliance Loss (₹)
                </label>

                <div className="font-bold text-blue-700">
                  {formatINR(leakage)}
                </div>
              </div>

              <input
                type="range"
                min="10000"
                max="5000000"
                step="10000"
                value={leakage}
                onChange={(e) => setLeakage(Number(e.target.value))}
                className="w-full accent-blue-600"
              />
            </div>

            {/* DEPLOYMENT */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <label className="text-sm font-semibold">
                  Deployment Preference
                </label>

                <div className="font-bold text-blue-700">
                  {deploymentMode === "cloud"
                    ? "Cloud"
                    : "On-Premise"}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setDeploymentMode("cloud")}
                  className={`rounded-2xl py-3 text-sm font-semibold transition-all ${
                    deploymentMode === "cloud"
                      ? "bg-blue-600 text-white shadow-lg"
                      : "border border-slate-300 bg-white text-slate-800"
                  }`}
                >
                  <i className="fa-solid fa-cloud mr-2"></i>
                  Cloud
                </button>

                <button
                  onClick={() => setDeploymentMode("onprem")}
                  className={`rounded-2xl py-3 text-sm font-semibold transition-all ${
                    deploymentMode === "onprem"
                      ? "bg-blue-600 text-white shadow-lg"
                      : "border border-slate-300 bg-white text-slate-800"
                  }`}
                >
                  <i className="fa-solid fa-building-columns mr-2"></i>
                  On-Premise
                </button>
              </div>

              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                Banks and regulated institutions often prefer On-Premise
                deployment for stronger governance, internal security controls
                and regulatory comfort.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI REPORT */}
      <section ref={reportRef} className="px-4 pb-8">
        <div className="rounded-[32px] bg-gradient-to-br from-slate-950 via-slate-900 to-blue-700 p-5 text-white shadow-2xl">
          <div className="mb-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
                <h2 className="text-2xl font-extrabold text-yellow-200">
                    ROI Report
                </h2>

                <p className="mt-1 text-sm text-blue-100">
                    Estimated Business Impact
                </p>
            </div>    
            <div className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold">
              LIVE
            </div>     
          </div> 
          <div className="mb-2 flex flex-col gap-3">
            <div className="mb-2 rounded-3xl bg-white/10 p-5 text-white">
                <h3 className="text-lg font-bold text-yellow-200 mb-3">
                    Input Values
                </h3>
                <div className="grid grid-cols-2 gap-3 text-xs text-blue-100">
                    <div className="bg-white/5 p-3 rounded-xl">
                        <p className="text-slate-300">Monthly Requests</p>
                        <p className="font-bold text-white">
                            {requests.toLocaleString("en-IN")}
                        </p>
                    </div>

                    <div className="bg-white/5 p-3 rounded-xl">
                        <p className="text-slate-300">Cost / Request</p>
                        <p className="font-bold text-white">
                            {formatINR(cost)}
                        </p>
                    </div>

                    <div className="bg-white/5 p-3 rounded-xl">
                        <p className="text-slate-300">Efficiency Gain</p>
                        <p className="font-bold text-white">
                            {reduction}%
                        </p>
                    </div>

                    <div className="bg-white/5 p-3 rounded-xl">
                        <p className="text-slate-300">Employees</p>
                        <p className="font-bold text-white">
                            {employees}
                        </p>
                    </div>

                    <div className="bg-white/5 p-3 rounded-xl col-span-2">
                        <p className="text-slate-300">Monthly Leakage</p>
                        <p className="font-bold text-white">
                            {formatINR(leakage)}
                        </p>
                    </div>

                    <div className="bg-white/5 p-3 rounded-xl col-span-2">
                        <p className="text-slate-300">Deployment Mode</p>
                        <p className="font-bold text-white">
                            {deploymentMode === "cloud" ? "Cloud" : "On-Premise"}
                        </p>
                    </div>
                </div>
            </div>
            {/* ACTION BUTTONS */}
          </div>

          {/* GRID */}
          <div className="grid grid-cols-2 gap-4">
            {/* CARD */}
            {[
              {
                title: "Current Monthly Operational Cost",
                value: formatINR(report.currentCost),
                color: "",
                icon: "fa-wallet",
              },
              {
                title: "Estimated Monthly Savings",
                value: formatINR(report.monthlySavings),
                color: "text-green-300",
                icon: "fa-piggy-bank",
              },
              {
                title: "Estimated Annual Savings",
                value: formatINR(report.annualSavings),
                color: "text-yellow-300",
                icon: "fa-chart-line",
              },
              {
                title: "Governance Risk Reduction",
                value: formatINR(report.riskReduction),
                color: "text-cyan-300",
                icon: "fa-shield-halved",
              },
              {
                title: "Productivity Gain",
                value: `${report.productivity} FTE Hrs/Day`,
                color: "",
                icon: "fa-bolt",
              },
              {
                title: "Estimated ROI",
                value: `${report.roi}%`,
                color: "",
                icon: "fa-chart-pie",
                green: true,
              },
              {
                title: "Estimated Platform Cost",
                value: formatINR(report.platformCost),
                color: "text-pink-300",
                icon: "fa-server",
              },
              {
                title: "Recommended Deployment",
                value: report.deploymentText,
                color: "",
                icon: "fa-database",
              },
            ].map((item, index) => (
              <div
                key={index}
                className={`rounded-3xl p-4 ${
                  item.green
                    ? "bg-green-500"
                    : "bg-white/10"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs text-blue-100">
                    {item.title}
                  </div>

                  <i className={`fa-solid ${item.icon} text-lg`}></i>
                </div>

                <div
                  className={`mt-3 text-2xl font-extrabold ${item.color}`}
                >
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          {/* AI RECOMMENDATION */}
          <div className="mt-5 rounded-3xl bg-white/10 p-5">
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-brain text-xl"></i>

              <h3 className="text-lg font-bold text-cyan-200">
                AI Recommendation
              </h3>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-blue-100">
              {report.recommendation}
            </p>
          </div>

          {/* DEPLOYMENT INSIGHT */}
          <div className="mt-5 rounded-3xl bg-white/10 p-5">
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-lock text-xl"></i>

              <h3 className="text-lg font-bold text-cyan-200">
                Deployment Insights
              </h3>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-blue-100">
              {report.deploymentInsight}
            </p>
          </div>
          <div className="mt-4 flex gap-2">
            <button
                onClick={downloadPDF}
                className="w-1/2 bg-green-600 py-2 rounded-xl text-xs lg:text-sm font-semibold"
            >
                <i className="fa-solid fa-download mr-2"></i>
                Download
            </button>

            <button
                onClick={shareReport}
                className="w-1/2 bg-blue-600 py-2 rounded-xl text-xs lg:text-sm font-semibold"
            >
                <i className="fa-solid fa-share mr-2"></i>
                Share
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <section className="bg-slate-950 px-4 py-2 text-white">
        <div className="space-y-4">
          <h2 className="text-2xl font-extrabold text-yellow-200">
            Anugya
          </h2>

          <p className="text-sm leading-relaxed text-slate-200">
            Consent OS for Human Records, Secure Actions,
            Governed Workflows and Trust Infrastructure.
          </p>
          
          <div className="border-t border-white/10 pt-4 text-xs text-slate-300">
            © 2026 Anugya Infrastructure Layer · Powered by Think5Whys
          </div>
        </div>
      </section>
    </div>
  );
};

export default AnugyaROICalc;